// Core
import React from 'react';

// Libraries
import { useTheme } from 'styled-components';
import {
  AdvancedMarker,
  APIProvider,
  Map as MapVis,
  useMap,
  useMapsLibrary,
} from '@vis.gl/react-google-maps';

// Types
import { Theme } from '../../../types/theme';

// Component
import { CoordinatesType, MapProps, PolygonsProps } from './Map.types';
import { Container } from './Map.style';

// Variables & Constants
const API_KEY: string | undefined = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const MAP_ID: string | undefined = process.env.REACT_APP_GOOGLE_MAPS_MAP_ID;
const LIBRARIES: ('marker' | 'maps')[] = ['marker', 'maps'];

function Polygons({
  paths,
  fillColor,
  fillOpacity,
  strokeColor,
  strokeWeight,
}: PolygonsProps) {
  const map: google.maps.Map | null = useMap();

  const maps: google.maps.MapsLibrary | null = useMapsLibrary('maps');

  React.useEffect(() => {
    if (map && maps) {
      const polygon: google.maps.Polygon = new maps.Polygon({
        paths,
        fillColor,
        fillOpacity,
        strokeColor,
        strokeWeight,
      });

      polygon.setMap(map);

      return (): void => {
        if (polygon) {
          polygon.setMap(null);
        }
      };
    }

    return undefined;
  }, [
    fillColor,
    fillOpacity,
    map,
    maps,
    paths,
    strokeColor,
    strokeWeight,
  ]);

  return null;
}

function Map({
  centerCoordinates,
  className,
  polygons,
  pins,
  testId,
}: MapProps): JSX.Element {
  const theme: Theme = useTheme();

  if (!API_KEY) {
    throw new Error('Google Maps API key is missing');
  } else if (!MAP_ID) {
    throw new Error('Google Maps Map ID is missing');
  }

  if (!centerCoordinates) {
    return (
      <Container
        className={className}
        data-testid={testId}
      />
    );
  }

  return (
    <Container
      className={className}
      data-testid={testId}
    >
      <APIProvider apiKey={API_KEY} libraries={LIBRARIES}>
        <MapVis
          key={`${centerCoordinates.lat}-${centerCoordinates.lng}`}
          defaultCenter={centerCoordinates}
          defaultZoom={18}
          mapId={MAP_ID}
          mapTypeId="satellite"
          fullscreenControl={false}
          mapTypeControl={false}
          streetViewControl={false}
          zoomControl={false}
        >
          {polygons && (
            <Polygons
              paths={polygons}
              fillColor={theme.color.mapView.fillColor}
              fillOpacity={0.1}
              strokeColor={theme.color.mapView.strokeColor}
              strokeWeight={2}
            />
          )}
          {pins && pins.map((coordinate: CoordinatesType) => (
            <AdvancedMarker
              key={`${coordinate.lat}-${coordinate.lng}`}
              position={coordinate}
            />
          ))}
        </MapVis>
      </APIProvider>
    </Container>
  );
}

export { Map };
